import React, { useEffect, useState } from 'react';
import {
    useForm,
    FormProvider,
    Controller,
    useFieldArray,
} from 'react-hook-form';
import Drawer from '~/components/Drawer';
import { sectionStyles, getSelectStyles } from '../styles';
import {
    FormInput,
    formatPhoneNumber,
    inputStylesBase,
    getInputBorderClass,
    labelStyles,
    requiredStyles,
} from '~/common/form/FormInput';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { FaPlus } from 'react-icons/fa6';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import {
    fetchRoles,
    fetchDepartments,
    updateUser,
    resetUserPassword,
} from '~/services/users';
import { PhoneNumberTypes, phoneNumberTypesList } from '~/enums/user';
import { useUserContext } from '~/context/UserProvider';
import Select from 'react-select';
import { startCase } from 'lodash';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
type Contact = {
    phone: string;
    type: PhoneNumberTypes;
};
type Department = {
    name: string;
    role: string;
};
type DefaultValues = {
    first_name: string;
    middle_name: string;
    last_name: string;
    contacts: Contact[];
    email: string;
    roles: string[];
    departments: Department[];
};

type UserRoleType = {
    id: number;
    name: string;
};

const departmentRoles = ['Manager', 'Member'];

const defaultValues: DefaultValues = {
    first_name: '',
    middle_name: '',
    last_name: '',
    contacts: [],
    email: '',
    roles: [],
    departments: [],
};

// Define types for phone set from the user object
interface PhoneEntry {
    phone: string;
    type: string;
}

// Define form data type
interface FormData extends DefaultValues {}

const UserDetailsDrawer: React.FC<Props> = ({ isOpen, setIsOpen }) => {
    const { user, userDispatch } = useUserContext();
    const [roles, setRoles] = useState<UserRoleType[]>([]);
    const [departmentsList, setDepartmentsList] = useState<
        { id: number; name: string }[]
    >([]);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [resetting, setResetting] = useState(false);

    const methods = useForm({
        defaultValues,
    });
    const {
        control,
        reset,
        formState: { isSubmitting },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'contacts',
    });
    const { fields: departmentFields, replace } = useFieldArray({
        control,
        name: 'departments',
    });

    const fetchUserRoles = async () => {
        try {
            const res = await fetchRoles();
            const excluded_roles = [
                'patient',
                'medical professional',
                'full team member',
                'limited team member',
                'no access team member',
                'emergency_contact',
            ];
            setRoles(
                res.filter(
                    (role: UserRoleType) => !excluded_roles.includes(role.name),
                ),
            );
        } catch (error) {
            console.log(error);
        }
    };

    // Departments come from the API rather than a literal in this file: the
    // literal had drifted from the table, and any name it got wrong was
    // silently dropped on save (HHLTB-2665).
    const fetchDepartmentsList = async () => {
        try {
            setDepartmentsList(await fetchDepartments());
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    useEffect(() => {
        fetchUserRoles();
        fetchDepartmentsList();
    }, []);

    useEffect(() => {
        if (user) {
            const formattedPhones = user.person?.phone_set?.map(
                (u: PhoneEntry) => {
                    return {
                        phone: formatPhoneNumber(u.phone || ''),
                        type: u.type || '',
                    };
                },
            );

            // The API sends null rather than [] for a user in no departments.
            const userDepartments = user.departments || [];

            reset({
                first_name: user.person.first_name,
                middle_name: user.person.middle_name,
                last_name: user.person.last_name,
                contacts: formattedPhones,
                email: user.person.email,
                roles: user.roles,
                departments: userDepartments,
            });
            replace(userDepartments);
        }
    }, [user, reset, replace]);

    const onDepartmentSelect = (departmentName: string, role: string) => {
        const currentDepartments = [...departmentFields];

        const existingIndex = currentDepartments.findIndex(
            (dept) => dept.name === departmentName,
        );

        if (existingIndex >= 0) {
            if (currentDepartments[existingIndex].role === role) {
                currentDepartments.splice(existingIndex, 1);
            } else {
                currentDepartments[existingIndex].role = role;
            }
        } else {
            currentDepartments.push({
                name: departmentName,
                role,
                id: uuidv4(),
            });
        }

        replace(currentDepartments);
    };

    const onSubmit = async (data: FormData) => {
        try {
            // Transform contacts to phone_set for the API
            const { contacts, ...rest } = data;
            const payload = {
                ...rest,
                phone_set: contacts,
            };
            const updatedUser = await updateUser(user?.id, payload);
            userDispatch({
                type: 'set_user',
                payload: updatedUser,
            });
            toast.success('User updated successfully');
            setIsOpen(false);
        } catch (error: any) {
            console.error('Error submitting form:', error);
            toast.error(
                error?.message || 'Error updating user. Please try again.',
            );
        }
    };

    const resetPassword = async () => {
        if (!user?.id) return;

        try {
            setResetting(true);
            await resetUserPassword(user.id);
            setResetEmailSent(true);
            toast.success('Password reset email sent');
            // Reset the state after 3 seconds
            setTimeout(() => setResetEmailSent(false), 3000);
        } catch (error: any) {
            console.error('Error resetting password:', error);
            toast.error(
                error?.message || 'Error sending password reset email.',
            );
        } finally {
            setResetting(false);
        }
    };

    return (
        <>
            <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='User Profile'>
                <FormProvider {...methods}>
                    <form
                        onSubmit={methods.handleSubmit((data) =>
                            onSubmit(data),
                        )}
                    >
                        <h2 className={sectionStyles}>CONTACT</h2>

                        {/* Using FormInput for simple text fields */}
                        <FormInput
                            name='first_name'
                            label='First Name'
                            required
                        />
                        <FormInput name='middle_name' label='Middle Name' />
                        <FormInput
                            name='last_name'
                            label='Last Name'
                            required
                        />

                        {/* Phone numbers - manual implementation for field array */}
                        {fields.map((contact, index) => (
                            <div
                                key={contact.id}
                                className='flex items-center gap-[5px]'
                            >
                                <div className='w-40'>
                                    <label
                                        htmlFor={`contacts.${index}.phone`}
                                        className={labelStyles}
                                    >
                                        Contact Number
                                    </label>
                                    <Controller
                                        name={`contacts.${index}.phone`}
                                        control={control}
                                        rules={{
                                            required: true,
                                            pattern: {
                                                value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                                message:
                                                    'Please enter a valid phone number in format (123) 456-7890',
                                            },
                                        }}
                                        render={({ field }) => (
                                            <input
                                                id={`contacts.${index}.phone`}
                                                type='tel'
                                                placeholder='(123) 456-7890'
                                                maxLength={14}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        formatPhoneNumber(
                                                            e.target.value,
                                                        ),
                                                    )
                                                }
                                                value={field.value}
                                                className={`${inputStylesBase} ${getInputBorderClass(
                                                    field.value,
                                                )} w-full`}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='w-30'>
                                    <label
                                        htmlFor={`contacts.${index}.type`}
                                        className={`${labelStyles} w-full grow`}
                                    >
                                        Type
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <Controller
                                        control={control}
                                        name={`contacts.${index}.type`}
                                        render={({ field }) => (
                                            <select
                                                id={`contacts.${index}.type`}
                                                className={`${inputStylesBase} w-full bg-transparent ${
                                                    field.value
                                                        ? 'border-black'
                                                        : 'border-gray-300'
                                                }`}
                                                {...field}
                                            >
                                                <option value='' disabled>
                                                    Select
                                                </option>
                                                {phoneNumberTypesList.map(
                                                    (t, i) => (
                                                        <option
                                                            value={t}
                                                            key={`phone_${i}`}
                                                        >
                                                            {t}
                                                        </option>
                                                    ),
                                                )}
                                            </select>
                                        )}
                                    />
                                </div>
                                {index !== 0 && (
                                    <div className='mt-[22px]'>
                                        <RiDeleteBin6Line
                                            color='#FF0000'
                                            size={'20px'}
                                            onClick={() => remove(index)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                        <button
                            type='button'
                            className='mt-3 flex items-center gap-1 text-sm font-medium text-[#00aeb3]'
                            onClick={() => {
                                append({
                                    phone: '',
                                    type: PhoneNumberTypes.OFFICE,
                                });
                            }}
                        >
                            <FaPlus />
                            ADD PHONE NUMBER
                        </button>

                        <FormInput name='email' label='Email' type='email' />

                        {user?.is_superuser && (
                            <div className='mt-[20px] rounded-md border-l-4 border-[#7C3AED] bg-[#F3EEFF] p-[16px]'>
                                <div className='inline-flex rounded-3xl bg-[#7C3AED] px-2 py-1 text-[12px]/[14.4px] font-semibold text-white'>
                                    Super Admin
                                </div>
                                <p className='mt-[8px] text-[13px]/[18px] text-[#131212]'>
                                    This user has superuser access, which
                                    includes the ability to view any
                                    client&apos;s portal via campaign
                                    impersonation. Cross-check this when auditing
                                    who can access client accounts.
                                </p>
                            </div>
                        )}

                        <h2 className={sectionStyles}>ROLE</h2>

                        <label
                            htmlFor='roles'
                            className={`${labelStyles} w-full grow`}
                        >
                            Roles<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            control={control}
                            name='roles'
                            render={({ field }) => (
                                <Select
                                    menuPlacement='auto'
                                    isMulti
                                    name='roles'
                                    options={roles.map((role) => ({
                                        value: role.name,
                                        label: startCase(role.name),
                                    }))}
                                    onChange={(selectedOptions) => {
                                        field.onChange(
                                            selectedOptions
                                                ? selectedOptions.map(
                                                      (option) => option.value,
                                                  )
                                                : [],
                                        );
                                    }}
                                    value={
                                        field.value
                                            ? field.value.map(
                                                  (role: string) => ({
                                                      value: role,
                                                      label: startCase(role),
                                                  }),
                                              )
                                            : []
                                    }
                                    className='w-full'
                                    styles={getSelectStyles(
                                        field.value && field.value.length > 0,
                                    )}
                                />
                            )}
                        />

                        <h2 className={sectionStyles}>DEPARTMENTS</h2>

                        {departmentsList.map((dept) => {
                            const deptFound = departmentFields.find(
                                (dep) => dept.name === dep.name,
                            );

                            return (
                                <div
                                    key={dept.name}
                                    className='mt-2 flex flex-wrap items-center justify-between border-b border-[#e5e5e5] pb-4'
                                >
                                    <h3
                                        className={`w-[160px] text-sm ${
                                            deptFound && 'font-semibold'
                                        }`}
                                    >
                                        {dept.name}
                                    </h3>
                                    <div className='flex space-x-4'>
                                        {departmentRoles.map((option) => (
                                            <Controller
                                                key={option}
                                                name='departments'
                                                control={control}
                                                render={({ field }) => (
                                                    <label
                                                        className={`cursor-pointer rounded-full border px-3 py-1 text-sm ${
                                                            deptFound?.role ===
                                                            option
                                                                ? 'border-[#00aeb3] bg-[#00aeb3] text-black'
                                                                : 'bg-white text-black'
                                                        }`}
                                                    >
                                                        <input
                                                            type='radio'
                                                            value={option}
                                                            onChange={(e) => {
                                                                field.onChange(
                                                                    e.target
                                                                        .value,
                                                                );
                                                                onDepartmentSelect(
                                                                    dept.name,
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            onClick={() => {
                                                                if (
                                                                    deptFound?.role ===
                                                                    option
                                                                ) {
                                                                    onDepartmentSelect(
                                                                        dept.name,
                                                                        option,
                                                                    );
                                                                }
                                                            }}
                                                            checked={
                                                                deptFound?.role ===
                                                                option
                                                            }
                                                            className='hidden'
                                                        />
                                                        {option}
                                                    </label>
                                                )}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}

                        <div className='mt-[30px] flex flex-wrap gap-[16px]'>
                            <button
                                type='button'
                                onClick={() => setIsOpen(false)}
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                disabled={isSubmitting || resetting}
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                onClick={resetPassword}
                                className='rounded-full border-[2px] border-[#00aeb3] bg-[#00aeb3] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:border-[#007d7f] hover:bg-[#007d7f]'
                                disabled={
                                    isSubmitting || resetEmailSent || resetting
                                }
                            >
                                {resetEmailSent
                                    ? 'Email Sent!'
                                    : resetting
                                    ? 'Sending...'
                                    : 'Reset Password'}
                            </button>
                            <button
                                type='submit'
                                className='w-22 rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                disabled={isSubmitting || resetting}
                            >
                                {isSubmitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </FormProvider>
            </Drawer>
        </>
    );
};

export default UserDetailsDrawer;
